import { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

// Check for URL auth params BEFORE React renders
// This runs synchronously during module load
const getInitialAuthState = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const authToken = urlParams.get('__auth_token');
  const passedTabId = urlParams.get('__tab_id');
  
  if (authToken && passedTabId) {
    // Set the tab ID from the opener
    window.__HERCHAIN_TAB_ID__ = passedTabId;
    sessionStorage.setItem('herchain_tab_id', passedTabId);
    
    // Store the token using the new tab ID
    const newStorageKey = `herchain_session_${passedTabId}`;
    sessionStorage.setItem(newStorageKey, authToken);
    
    // Clean URL by removing auth params (do this after storing)
    const cleanUrl = window.location.pathname;
    window.history.replaceState({}, document.title, cleanUrl);
    
    return { token: authToken, tabId: passedTabId, fromUrl: true };
  }
  
  return { token: null, tabId: null, fromUrl: false };
};

// Run this synchronously before component mounts
const initialAuthState = getInitialAuthState();

// Generate or retrieve a unique tab ID for this browser tab instance
// Uses sessionStorage to persist across page refreshes within the same tab
const generateTabId = () => {
  // First check if we have it in memory (for SPA navigation)
  if (window.__HERCHAIN_TAB_ID__) {
    return window.__HERCHAIN_TAB_ID__;
  }
  
  // Then check sessionStorage (persists across page refresh)
  const storedTabId = sessionStorage.getItem('herchain_tab_id');
  if (storedTabId) {
    window.__HERCHAIN_TAB_ID__ = storedTabId;
    return storedTabId;
  }
  
  // Generate a new unique ID for this tab
  const tabId = `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  window.__HERCHAIN_TAB_ID__ = tabId;
  sessionStorage.setItem('herchain_tab_id', tabId);
  return tabId;
};

// Get the unique storage key for this tab's session
const getStorageKey = () => `herchain_session_${generateTabId()}`;

// Tab-isolated session storage functions
const getStoredToken = () => {
  // If we got token from URL, use that
  if (initialAuthState.fromUrl && initialAuthState.token) {
    return initialAuthState.token;
  }
  const key = getStorageKey();
  return sessionStorage.getItem(key);
};

const setStoredToken = (token) => {
  const key = getStorageKey();
  sessionStorage.setItem(key, token);
};

const removeStoredToken = () => {
  const key = getStorageKey();
  sessionStorage.removeItem(key);
};

// Store the user role for this tab (for debugging/verification)
const setStoredRole = (role) => {
  const key = `herchain_role_${generateTabId()}`;
  sessionStorage.setItem(key, role);
};

const getStoredRole = () => {
  const key = `herchain_role_${generateTabId()}`;
  return sessionStorage.getItem(key);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(getStoredToken());
  const [loading, setLoading] = useState(true);
  const tabIdRef = useRef(generateTabId());

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      // Store the role for this tab
      if (response.data?.role) {
        setStoredRole(response.data.role);
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    setStoredToken(newToken);
    setToken(newToken);
    setUser(userData);
    // Store the role for this tab
    if (userData?.role) {
      setStoredRole(userData.role);
    }
    return userData;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API_URL}/api/auth/register`, userData);
    const { token: newToken, user: newUser } = response.data;
    setStoredToken(newToken);
    setToken(newToken);
    setUser(newUser);
    if (newUser?.role) {
      setStoredRole(newUser.role);
    }
    return newUser;
  };

  const logout = () => {
    removeStoredToken();
    setToken(null);
    setUser(null);
  };

  const updateCurrency = async (currency) => {
    const response = await axios.put(
      `${API_URL}/api/auth/currency?currency=${currency}`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setUser(response.data);
    return response.data;
  };

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${token}` }
  });

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      register,
      logout,
      updateCurrency,
      getAuthHeaders,
      isAuthenticated: !!user,
      tabId: tabIdRef.current
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
